import React, { useState } from 'react';
import { apiService } from '../services/api';

interface JoinLeagueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  leagueId?: number; // Optional: if joining a specific league directly
  isPublicLeague?: boolean; // Optional: if joining a public league
  leagueType?: string; // Optional: league type (pickem, eliminator, budget_league, etc.)
}

const JoinLeagueModal: React.FC<JoinLeagueModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  leagueId,
  isPublicLeague = false,
  leagueType // No default - must be provided
}) => {
  const [joinCode, setJoinCode] = useState('');
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // For public leagues, we don't need join code
    if (!isPublicLeague && !joinCode.trim()) {
      setError('Join code is required for private leagues');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let data;
      
      if (leagueId) {
        // Joining a specific league by ID
        // For public leagues, don't send join code or passcode
        const code = isPublicLeague ? undefined : (joinCode.trim() || undefined);
        const pass = isPublicLeague ? undefined : (passcode.trim() || undefined);
        
        // Use appropriate API based on league type
        if (leagueType === 'eliminator') {
          data = await apiService.joinEliminatorLeague(leagueId, code, pass);
        } else if (leagueType === 'pickem') {
          data = await apiService.joinPickemLeague(leagueId, code, pass);
        } else {
          // For other league types (budget_league, draft_league, h2h, etc.), use generic fantasy league join
          // If passcode is provided, we need to use join-by-code endpoint (which supports passcode)
          // Otherwise, use join-by-id endpoint (which only supports join_code)
          if (pass && code) {
            // Both passcode and join_code provided - use join-by-code endpoint
            data = await apiService.joinLeagueByCode(code, pass);
          } else {
            // No passcode or no join_code - use join-by-id endpoint
            data = await apiService.joinFantasyLeague(leagueId, code);
          }
        }
      } else {
        // Joining by join code - use generic endpoint that works for all league types
        data = await apiService.joinLeagueByCode(joinCode.trim(), passcode.trim() || undefined);
      }
      
      if (data.success) {
        setSuccess(data.message || 'Successfully joined the league!');
        setJoinCode('');
        setPasscode('');
        
        // Close modal after 2 seconds and refresh leagues
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(data.message || 'Failed to join league');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to join league');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setJoinCode('');
    setPasscode('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">
            {isPublicLeague ? 'Join Public League' : 'Join Private League'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isPublicLeague && (
            <div>
              <label htmlFor="joinCode" className="block text-sm font-medium text-gray-300 mb-2">
                Join Code *
              </label>
              <input
                type="text"
                id="joinCode"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter join code"
                maxLength={10}
              />
            </div>
          )}

          {!isPublicLeague && (
            <div>
              <label htmlFor="passcode" className="block text-sm font-medium text-gray-300 mb-2">
                Passcode (if required)
              </label>
              <input
                type="text"
                id="passcode"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter passcode (optional)"
                maxLength={8}
              />
            </div>
          )}

          {error && (
            <div className="text-red-400 text-sm bg-red-900 bg-opacity-20 p-3 rounded-md">
              {error}
            </div>
          )}

          {success && (
            <div className="text-green-400 text-sm bg-green-900 bg-opacity-20 p-3 rounded-md">
              {success}
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!isPublicLeague && !joinCode.trim())}
              className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                loading || (!isPublicLeague && !joinCode.trim())
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {loading ? 'Joining...' : 'Join League'}
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 bg-gray-700 rounded-md">
          <p className="text-sm text-gray-300">
            <strong>How to join:</strong>
          </p>
          <ul className="text-xs text-gray-400 mt-2 space-y-1">
            {isPublicLeague ? (
              <>
                <li>• This is a public league - no join code needed</li>
                <li>• You can join directly if spots are available</li>
                <li>• League will appear in "My Leagues" after joining</li>
              </>
            ) : (
              <>
                <li>• Get the join code from the league manager</li>
                <li>• Enter the join code above</li>
                <li>• If it's a private league, you'll also need the passcode</li>
                <li>• The league will appear in "My Leagues" after joining</li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default JoinLeagueModal; 